/**
 * Simple rate limiter utility to prevent excessive API calls
 */

interface RateLimiterState {
  [key: string]: {
    count: number;
    windowStart: number;
  };
}

class RateLimiter {
  private state: RateLimiterState = {};
  private windowSize: number; // in milliseconds
  private maxRequests: number;

  constructor(maxRequests: number = 10, windowSizeMs: number = 60000) {
    this.maxRequests = maxRequests;
    this.windowSize = windowSizeMs;
  }

  /**
   * Check if a request is allowed for the given key
   * @param key - Unique identifier for the rate limiting (e.g., operation name)
   * @returns true if request is allowed, false otherwise
   */
  isAllowed(key: string): boolean {
    const now = Date.now();
    const keyState = this.state[key];

    // If no previous requests or window has expired
    if (!keyState || now - keyState.windowStart >= this.windowSize) {
      this.state[key] = {
        count: 1,
        windowStart: now,
      };
      return true;
    }

    // If within the window and under the limit
    if (keyState.count < this.maxRequests) {
      keyState.count++;
      return true;
    }

    // Rate limit exceeded
    return false;
  }

  /**
   * Get remaining requests for a key
   */
  getRemainingRequests(key: string): number {
    const keyState = this.state[key];
    if (!keyState) return this.maxRequests;
    
    const now = Date.now();
    if (now - keyState.windowStart >= this.windowSize) {
      return this.maxRequests;
    }
    
    return Math.max(0, this.maxRequests - keyState.count);
  }

  /**
   * Reset rate limit for a specific key
   */
  reset(key: string): void {
    delete this.state[key];
  }

  /**
   * Reset all rate limits
   */
  resetAll(): void {
    this.state = {};
  }
}

// Global rate limiter instance
export const globalRateLimiter = new RateLimiter(20, 60000); // 20 requests per minute

// Specific rate limiters for different operations
export const blogUpdateLimiter = new RateLimiter(5, 60000); // 5 blog updates per minute
export const viewIncrementLimiter = new RateLimiter(50, 60000); // 50 view increments per minute

export default RateLimiter;
